import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, IconButton, Dialog, DialogActions, DialogContent,
  DialogTitle, Button, Switch, TextField, Typography, Card,
  CardContent, Grid, Tabs, Tab, CircularProgress, Select, MenuItem,
  InputLabel, FormControl, Chip,
  Avatar
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Autocomplete from '@mui/material/Autocomplete';
import { BsTrash, BsDownload, BsPencil } from 'react-icons/bs';
import { toast } from 'react-toastify';
import * as XLSX from "xlsx";
import axios from 'axios';
import PageHeader1 from '../../../components/common/PageHeader1';
import debounce from 'lodash/debounce';

const convertLocalStringToUTC = (localStr) => {
  if (!localStr) return '';
  const [datePart, timePart] = localStr.split('T');
  if (!datePart || !timePart) return '';
  const [year, month, day] = datePart.split('-');
  const [hour, min] = timePart.split(':');
  return new Date(year, month - 1, day, hour, min).toISOString();
};

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60 * 1000;
  const localNow = new Date(now.getTime() - offsetMs);
  return localNow.toISOString().slice(0, 16);
};

function FreeChatAstrologersList() {
  const token = localStorage.getItem('astroVani-admin-token');

  // --- AppConfig Free Chat Time ---
  const [globalTime, setGlobalTime] = useState('');
  const [isUpdatingTime, setIsUpdatingTime] = useState(false);

  // --- AppConfig Human Free Chat Toggle ---
  const [isHumanFreeChatEnabled, setIsHumanFreeChatEnabled] = useState(false);
  const [isTogglingHumanChat, setIsTogglingHumanChat] = useState(false);

  // --- AppConfig Recharge Popup Time ---
  const [rechargePopupTime, setRechargePopupTime] = useState('');
  const [isUpdatingRechargePopupTime, setIsUpdatingRechargePopupTime] = useState(false);

  // --- Configured Astrologers State ---
  const [configData, setConfigData] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [searchConfigText, setSearchConfigText] = useState('');
  const [configPaginationModel, setConfigPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addType, setAddType] = useState('Astrologer');
  const [searchAstroOptions, setSearchAstroOptions] = useState([]);
  const [selectedAstrologers, setSelectedAstrologers] = useState([]);
  const [searchAstroLoading, setSearchAstroLoading] = useState(false);
  const [searchAstroPage, setSearchAstroPage] = useState(1);
  const [searchAstroTotalPages, setSearchAstroTotalPages] = useState(1);
  const [searchAstroText, setSearchAstroText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [astrologerToDelete, setAstrologerToDelete] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [astroToEdit, setAstroToEdit] = useState(null);
  const [editStartDateTime, setEditStartDateTime] = useState('');
  const [originalStartDateTime, setOriginalStartDateTime] = useState('');
  const [editEndDateTime, setEditEndDateTime] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  // 1. Fetch Global Time
  const fetchGlobalTime = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/freeChatTime`, {
        headers: { Authorization: token },
        params: {
          key: "freeChatTime"
        }
      });
      if (res.data?.success) {
        setGlobalTime(res.data.data?.value || 0);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateGlobalTime = async () => {
    if (!globalTime) return toast.error("Please enter a valid duration");
    setIsUpdatingTime(true);
    try {
      await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/freeChatTime`, { key: "freeChatTime", value: Number(globalTime), description: "Free session duration in seconds" }, {
        headers: { Authorization: token }
      });
      toast.success("Global Free Chat Duration updated successfully");
      fetchGlobalTime();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update duration");
    } finally {
      setIsUpdatingTime(false);
    }
  };

  const fetchHumanFreeChatStatus = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/config/isHumanFreeChatEnabled`, {
        headers: { Authorization: token }
      });
      if (res.data?.success) {
        setIsHumanFreeChatEnabled(res.data.data?.value === true || res.data.data?.value === "true");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleHumanFreeChat = async (e) => {
    const newValue = e.target.checked;
    setIsTogglingHumanChat(true);
    try {
      await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/isHumanFreeChatEnabled`, { value: newValue }, {
        headers: { Authorization: token }
      });
      toast.success("Human Free Chat status updated");
      setIsHumanFreeChatEnabled(newValue);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    } finally {
      setIsTogglingHumanChat(false);
    }
  };

  const fetchRechargePopupTime = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/config/rechargePopupTime`, {
        headers: { Authorization: token }
      });
      if (res.data?.success) {
        setRechargePopupTime(res.data.data?.value || 0);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateRechargePopupTime = async () => {
    if (!rechargePopupTime) return toast.error("Please enter a valid time");
    setIsUpdatingRechargePopupTime(true);
    try {
      await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/rechargePopupTime`, { value: Number(rechargePopupTime) }, {
        headers: { Authorization: token }
      });
      toast.success("Recharge Popup Time updated successfully");
      fetchRechargePopupTime();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update recharge popup time");
    } finally {
      setIsUpdatingRechargePopupTime(false);
    }
  };

  // 2. Fetch Configured Astrologers
  const fetchConfiguredAstro = async () => {
    setConfigLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/freeChatAstrologer`, {
        headers: { Authorization: token }
      });
      setConfigData(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch free chat astrologers");
    } finally {
      setConfigLoading(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/freeChatAstrologer/${id}`, { isActive: !currentStatus }, {
        headers: { Authorization: token }
      });
      toast.success("Status updated successfully");
      fetchConfiguredAstro();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDeleteAstro = (id) => {
    setAstrologerToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDeleteAstro = async () => {
    if (!astrologerToDelete) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/freeChatAstrologer/${astrologerToDelete}`, {
        headers: { Authorization: token }
      });
      toast.success("Astrologer removed successfully");
      fetchConfiguredAstro();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove astrologer");
    } finally {
      setDeleteModalOpen(false);
      setAstrologerToDelete(null);
    }
  };

  const handleEditAstro = (row) => {
    setAstroToEdit(row);
    let st = '';
    let et = '';
    if (row.startDate) st += row.startDate.split('T')[0];
    if (row.startTime) st += `T${row.startTime}`;
    if (row.endDate) et += row.endDate.split('T')[0];
    if (row.endTime) et += `T${row.endTime}`;
    
    setEditStartDateTime(st);
    setOriginalStartDateTime(st);
    setEditEndDateTime(et);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editStartDateTime) return toast.error("Start Date Time is required");
    if (!editEndDateTime) return toast.error("End Date Time is required");

    if (new Date(editStartDateTime) >= new Date(editEndDateTime)) {
      return toast.error('Start Date Time must be before End Date Time');
    }

    setIsEditing(true);
    try {
      const payload = {
        startDate: new Date(editStartDateTime.split('T')[0]).toISOString(),
        endDate: new Date(editEndDateTime.split('T')[0]).toISOString(),
        startTime: editStartDateTime.split('T')[1] || null,
        endTime: editEndDateTime.split('T')[1] || null,
      };
      await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/freeChatAstrologer/${astroToEdit._id}`, payload, {
        headers: { Authorization: token }
      });
      toast.success("Astrologer time updated successfully");
      setEditModalOpen(false);
      fetchConfiguredAstro();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update astrologer time");
    } finally {
      setIsEditing(false);
    }
  };

  // 3. Search Astrologers for Add Modal
  const fetchSearchAstrologers = async (searchText, page = 1) => {
    setSearchAstroLoading(true);
    try {
      let url = '';
      if (addType === 'Astrologer') {
        url = `${process.env.REACT_APP_BASEURL}/admin/getAllAstroVerified?page=${page}&limit=20&fullName=${encodeURIComponent(searchText || '')}&forFreeChatAstro=true`;
      } else {
        url = `${process.env.REACT_APP_BASEURL}/admin/ai-astrologer?page=${page}&limit=20&search=${encodeURIComponent(searchText || '')}`;
      }
      const res = await axios.get(url, { headers: { Authorization: token } });
      setSearchAstroOptions(res.data?.data || []);
      setSearchAstroTotalPages(res.data?.paginationDetail?.totalPages || 1);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchAstroLoading(false);
    }
  };

  const debouncedSearchAstro = useCallback(
    debounce((text) => {
      setSearchAstroPage(1);
      setSearchAstroText(text);
      fetchSearchAstrologers(text, 1);
    }, 500),
    [addType]
  );

  useEffect(() => {
    if (addModalOpen) {
      setSearchAstroPage(1);
      setSearchAstroText('');
      fetchSearchAstrologers('', 1);
      setStartDateTime(getCurrentDateTimeLocal());
      setEndDateTime('');
    }
  }, [addModalOpen, addType]);

  const handleAddSubmit = async () => {
    if (!selectedAstrologers.length) return toast.error("Please select at least one astrologer");
    if (!startDateTime) return toast.error("Start Date Time is required");
    if (!endDateTime) return toast.error("End Date Time is required");

    const now = new Date();
    const pastLimit = new Date(now.getTime() - 2 * 60000); // 2 minutes grace period for form submission

    if (new Date(startDateTime) < pastLimit) {
      return toast.error('Start Date Time cannot be in the past');
    }
    if (new Date(endDateTime) < pastLimit) {
      return toast.error('End Date Time cannot be in the past');
    }
    if (new Date(startDateTime) >= new Date(endDateTime)) {
      return toast.error('Start Date Time must be before End Date Time');
    }

    setIsAdding(true);
    try {
      const payload = {
        astrologerIds: selectedAstrologers.map(a => a._id || a.id),
        astrologerTypeRef: addType,
        startDate: new Date(startDateTime.split('T')[0]).toISOString(),
        endDate: new Date(endDateTime.split('T')[0]).toISOString(),
        startTime: startDateTime.split('T')[1] || null,
        endTime: endDateTime.split('T')[1] || null,
        isActive: true
      };
      await axios.post(`${process.env.REACT_APP_BASEURL}/admin/freeChatAstrologer`, payload, {
        headers: { Authorization: token }
      });
      toast.success("Astrologers added successfully");
      setAddModalOpen(false);
      setSelectedAstrologers([]);
      fetchConfiguredAstro();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add astrologers");
    } finally {
      setIsAdding(false);
    }
  };

  useEffect(() => {
    fetchGlobalTime();
    fetchConfiguredAstro();
    fetchHumanFreeChatStatus();
    fetchRechargePopupTime();
  }, []);

  const configColumns = [
    {
      field: 'name',
      headerName: 'Astrologer Name',
      flex: 1,
      minWidth: 230,
      renderCell: (params) => {
        const astro = params.row.astrologerId;
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {
              astro?.profileImg ?
                (<Avatar src={astro?.profileImg} />) :
                (<Avatar>{astro?.fullName?.charAt(0)}</Avatar>)
            }
            <Box>
              <Typography variant="body2">{astro?.fullName || astro?.name || '-'}</Typography>
              {(astro?.mobileNumber || astro?.email) && (
                <Typography variant="caption" color="textSecondary" display="block">
                  {astro?.mobileNumber || astro?.email}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }
    },
    { field: 'astrologerTypeRef', headerName: 'Type', width: 110, renderCell: (params) => <Chip label={params.value} size="small" color={params.value === 'AiAstrologer' ? 'primary' : 'secondary'} /> },
    { field: 'duration', headerName: 'Configured Time', width: 140, valueGetter: (value, row) => `${row.freeSessionDurationSeconds} sec` },
    {
      field: 'todayStats', headerName: 'Today Stats', width: 140, renderCell: (params) => (
        <Box>
          <Typography variant="caption" display="block">Count: {params.row.stats?.todayCount || 0}</Typography>
          <Typography variant="caption" display="block">Dur: {params.row.stats?.todayDurationSeconds || 0}s</Typography>
        </Box>
      )
    },
    {
      field: 'weeklyStats', headerName: 'Weekly Stats', width: 130, renderCell: (params) => (
        <Box>
          <Typography variant="caption" display="block">Count: {params.row.stats?.weeklyCount || 0}</Typography>
          <Typography variant="caption" display="block">Dur: {params.row.stats?.weeklyDurationSeconds || 0}s</Typography>
        </Box>
      )
    },
    {
      field: 'validity', headerName: 'Validity (Start - End)', width: 220, renderCell: (params) => {
        const formatDateTime = (dateStr, timeStr) => {
          if (!dateStr) return '-';
          const d = new Date(dateStr);
          let displayDate = d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
          if (timeStr) {
            const [hh, mm] = timeStr.split(':');
            const hr = parseInt(hh, 10);
            const ampm = hr >= 12 ? 'pm' : 'am';
            const hr12 = hr % 12 || 12;
            displayDate += `, ${hr12.toString().padStart(2, '0')}:${mm} ${ampm}`;
          }
          return displayDate;
        };
        const start = formatDateTime(params.row.startDate, params.row.startTime);
        const end = formatDateTime(params.row.endDate, params.row.endTime);
        return (
          <Box>
            <Typography variant="caption" display="block"><b>S:</b> {start}</Typography>
            <Typography variant="caption" display="block"><b>E:</b> {end}</Typography>
          </Box>
        );
      }
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 80,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          <Switch
            checked={params.row.isActive}
            onChange={() => handleToggleStatus(params.row._id, params.row.isActive)}
            color="success"
          />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <Box display="flex" gap={1} alignItems="center" height="100%">
          <IconButton color="primary" onClick={() => handleEditAstro(params.row)}>
            <BsPencil size={18} />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteAstro(params.row._id)}>
            <BsTrash size={18} />
          </IconButton>
        </Box>
      )
    }
  ];

  const filteredConfigData = configData.filter((item) => {
    if (!searchConfigText) return true;
    const searchLower = searchConfigText.toLowerCase();
    const astroName = item.astrologerId?.fullName || item.astrologerId?.name || '';
    return astroName.toLowerCase().includes(searchLower);
  });

  const handleExport = useCallback(async () => {
    setExportLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/freeChatAstrologerExport`, {
        headers: { Authorization: token }
      });
      const exportData = res.data?.data || res.data || [];
      if (!exportData.length) {
        toast.error("No data available to export");
        return;
      }

      const headers = [
        "Astrologer Name",
        "Email",
        "Mobile Number",
        "Configured Time (s)",
        "Created At",
        "Active Status",
        "Today Count",
        "Today Duration (s)",
        "Weekly Count",
        "Weekly Duration (s)",
        "Start Date",
        "Start Time",
        "End Date",
        "End Time"
      ];

      const rows = exportData.map(item => {
        const astroName = item.astrologerName || "Unknown";
        const email = item.email || '-';
        const mobileNumber = item.mobileNumber || '-';
        const configuredTime = item.freeSessionDurationSeconds || 0;
        const createdAt = item.createdAt || '-';
        const status = item.status || (item.isActive ? 'Active' : 'Inactive');
        const todayCount = item.todayFreeChatCount || 0;
        const todayDuration = item.todayDurationSeconds || 0;
        const weeklyCount = item.weeklyFreeChatCount || 0;
        const weeklyDuration = item.weeklyDurationSeconds || 0;
        const startDate = item.startDate || '-';
        const startTime = item.startTime || '-';
        const endDate = item.endDate || '-';
        const endTime = item.endTime || '-';

        return [
          astroName, email, mobileNumber, configuredTime, createdAt, status,
          todayCount, todayDuration, weeklyCount, weeklyDuration,
          startDate, startTime, endDate, endTime
        ];
      });

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "FreeChatAstrologers");

      const columnWidths = headers.map(header => ({ wch: header.length + 10 }));
      worksheet["!cols"] = columnWidths;

      XLSX.writeFile(workbook, "free_chat_astrologers.xlsx");
      toast.success("Exported successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  }, [token]);

  return (
    <div className="container-xxl">
      <PageHeader1 headerTitle="Free Chat Astrologers" isExport={false} />

      {/* Global Config Section */}
      <Card sx={{ mb: 3, boxShadow: 1, borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>Global AppConfig</Typography>
            {/* Toggle Row */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" fontWeight="500">Enable Human Free Chat:</Typography>
              <Switch
                checked={isHumanFreeChatEnabled}
                onChange={handleToggleHumanFreeChat}
                disabled={isTogglingHumanChat}
                color="primary"
              />
            </Box>
          </Box>
          <Grid container spacing={3} mt={1}>
            {/* Free Chat Duration */}
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField
                  label="Free Chat Duration (seconds)"
                  type="number"
                  size="small"
                  fullWidth
                  value={globalTime}
                  onChange={(e) => setGlobalTime(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateGlobalTime}
                  disabled={isUpdatingTime}
                  sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap', minWidth: { sm: 160 } }}
                >
                  {isUpdatingTime ? 'Updating...' : 'Update Duration'}
                </Button>
              </Box>
            </Grid>

            {/* Recharge Popup Time */}
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField
                  label="Recharge Popup Time (seconds)"
                  type="number"
                  size="small"
                  fullWidth
                  value={rechargePopupTime}
                  onChange={(e) => setRechargePopupTime(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateRechargePopupTime}
                  disabled={isUpdatingRechargePopupTime}
                  sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap', minWidth: { sm: 160 } }}
                >
                  {isUpdatingRechargePopupTime ? 'Updating...' : 'Update Time'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Box sx={{ width: '100%', bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 1 }}>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
            <Typography variant="h6">Eligible Free Chat Astrologers</Typography>
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                size="small"
                label="Search Astrologer"
                value={searchConfigText}
                onChange={(e) => setSearchConfigText(e.target.value)}
              />
              <Button variant="outlined" color="primary" onClick={handleExport} disabled={exportLoading} startIcon={exportLoading ? <CircularProgress size={20} /> : <BsDownload />}>
                {exportLoading ? 'Exporting...' : 'Export'}
              </Button>
              <Button variant="contained" color="secondary" onClick={() => { setAddType('Astrologer'); setSelectedAstrologers([]); setAddModalOpen(true); }}>
                + Add Free Chat Astro
              </Button>
            </Box>
          </Box>
          <DataGrid
            rows={filteredConfigData}
            columns={configColumns}
            getRowId={(row) => row._id}
            loading={configLoading}
            autoHeight
            disableRowSelectionOnClick
            paginationModel={configPaginationModel}
            onPaginationModelChange={setConfigPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            pagination
          />
        </Box>
      </Box>

      {/* Add Modal */}
      <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Free Chat Astrologers</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} pt={1}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Astrologer Type</InputLabel>
              <Select
                value={addType}
                label="Astrologer Type"
                disabled
                onChange={(e) => {
                  setAddType(e.target.value);
                  setSelectedAstrologers([]);
                  setSearchAstroOptions([]);
                }}
              >
                <MenuItem value="Astrologer">Human Astrologer</MenuItem>
                <MenuItem value="AiAstrologer">AI Astrologer</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              options={searchAstroOptions}
              getOptionLabel={(option) => option.fullName || option.name || option._id}
              value={selectedAstrologers}
              onChange={(e, newVal) => setSelectedAstrologers(newVal)}
              onInputChange={(e, newInputValue) => debouncedSearchAstro(newInputValue)}
              loading={searchAstroLoading}
              PaperComponent={({ children, ...props }) => (
                <Box {...props} component="div" sx={{ backgroundColor: 'background.paper', borderRadius: 1, boxShadow: 3 }}>
                  {children}
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} p={1} borderTop="1px solid #eee" onMouseDown={(e) => e.preventDefault()}>
                    <Button
                      size="small"
                      disabled={searchAstroPage <= 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        const newPage = searchAstroPage - 1;
                        setSearchAstroPage(newPage);
                        fetchSearchAstrologers(searchAstroText, newPage);
                      }}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2">
                      Page {searchAstroPage} / {searchAstroTotalPages}
                    </Typography>
                    <Button
                      size="small"
                      disabled={searchAstroPage >= searchAstroTotalPages}
                      onClick={(e) => {
                        e.stopPropagation();
                        const newPage = searchAstroPage + 1;
                        setSearchAstroPage(newPage);
                        fetchSearchAstrologers(searchAstroText, newPage);
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Search ${addType === 'Astrologer' ? 'Human' : 'AI'} Astrologer`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchAstroLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Start Date Time"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: getCurrentDateTimeLocal() }}
                value={startDateTime}
                onChange={(e) => {
                  const val = e.target.value;
                  const currentStr = getCurrentDateTimeLocal();
                  if (val && val < currentStr) {
                    toast.error("Past date/time is not allowed");
                    setStartDateTime(currentStr);
                  } else {
                    setStartDateTime(val);
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="End Date Time"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDateTime || getCurrentDateTimeLocal() }}
                value={endDateTime}
                onChange={(e) => {
                  const val = e.target.value;
                  const minStr = startDateTime || getCurrentDateTimeLocal();
                  if (val && val < minStr) {
                    toast.error("End date/time cannot be before start date/time");
                    setEndDateTime(minStr);
                  } else {
                    setEndDateTime(val);
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleAddSubmit} disabled={isAdding || selectedAstrologers.length === 0}>
            {isAdding ? 'Adding...' : 'Add Selected'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Free Chat Validity</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" gap={2} pt={1}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Start Date Time"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: originalStartDateTime }}
              value={editStartDateTime}
              onChange={(e) => {
                const val = e.target.value;
                if (val && val < originalStartDateTime) {
                  toast.error("Start date/time cannot be less than original start date/time");
                  setEditStartDateTime(originalStartDateTime);
                } else {
                  setEditStartDateTime(val);
                }
              }}
            />
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="End Date Time"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: editStartDateTime }}
              value={editEndDateTime}
              onChange={(e) => {
                const val = e.target.value;
                if (val && val <= editStartDateTime) {
                  toast.error("End date/time must be greater than start date/time");
                  // Optional: Reset it to what it was, or reset to min
                  setEditEndDateTime(editStartDateTime);
                } else {
                  setEditEndDateTime(val);
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleEditSubmit} disabled={isEditing}>
            {isEditing ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to remove this astrologer from Free Chat?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteAstro}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FreeChatAstrologersList;
